import { createClient } from "@stringiverse/trpc-client";
import { useCallback, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { ClientOnly } from "vike-react/ClientOnly";
import { Button } from "#components/Button.tsx";
import Checkbox from "#components/Checkbox.tsx";
import Input from "#components/Input.tsx";
import Notify from "#components/Notify.tsx";
import Turnstile from "#components/Turnstile.tsx";
import { AlertAccent, ThemeButtonVariant } from "#components/theme.ts";
import { isStringiverseTRPCError } from "#lib/trpc.ts";
import { useRPC } from "#store/trpc.ts";
import AuthForm from "../AuthForm.tsx";

export default function Page() {
	const client = useRPC();
	type RegisterForm = Parameters<
		(typeof client)["auth"]["register"]["mutate"]
	>[0];

	const {
		register,
		handleSubmit,
		formState: { errors, isValid, isSubmitting },
		setValue,
		resetField,
	} = useForm<RegisterForm>({
		mode: "onTouched",
	});
	const [formError, setFormError] = useState<string | null>(null);

	const onSubmit: SubmitHandler<RegisterForm> = async (data) => {
		try {
			setFormError(null);
			await createClient().auth.register.mutate(data);
			window.location.href = "/onboarding";
		} catch (e) {
			if (isStringiverseTRPCError(e)) {
				setFormError(e.message);
				for (const field of e.shape.fields ?? []) {
					setValue(
						field as keyof RegisterForm,
						data[field as keyof RegisterForm],
					);
				}
			} else {
				setFormError(`An unknown error occurred: ${e?.toString()}`);
			}
		}
	};

	const setToken = useCallback(
		(token: string | null) =>
			token
				? setValue("cf-turnstile-response", token)
				: resetField("cf-turnstile-response"),
		[setValue, resetField],
	);

	return (
		<AuthForm
			onSubmit={handleSubmit(onSubmit)}
			title="Agent Application Form"
			formError={formError}
			before={
				<Notify accent={AlertAccent.Warning} className="mb-4">
					<p>
						Agents, please be sure to keep your contact information up to date
						so as to be prepared for upcoming missions.
					</p>
					<p>
						Should we attempt to reach you and you are unavailable, the mission
						will be assigned to another agent.
					</p>
				</Notify>
			}
		>
			<div className="grid grid-cols-2 gap-3">
				<Input
					className="w-full"
					type="text"
					id="firstName"
					placeholder="First Name"
					minLength={2}
					maxLength={25}
					required
					{...register("firstName", {
						required: { value: true, message: "First name is required" },
						minLength: {
							value: 2,
							message: "First name must be at least 2 letters",
						},
						maxLength: {
							value: 25,
							message: "First name must be at most 25 letters",
						},
						pattern: /^[\w ]*$/,
					})}
					error={errors.firstName}
				/>
				<Input
					className="w-full"
					type="text"
					placeholder="Last Name"
					id="lastName"
					minLength={1}
					maxLength={25}
					required
					{...register("lastName", {
						required: { value: true, message: "Last name is required" },
						minLength: {
							value: 1,
							message: "Last name must be at least 1 letter",
						},
						maxLength: {
							value: 25,
							message: "Last name must be at most 25 letters",
						},
						pattern: /^[\w ]*$/,
					})}
					error={errors.lastName}
				/>
			</div>
			<Input
				type="email"
				className="w-full"
				placeholder="Email"
				id="email"
				maxLength={80}
				required
				{...register("email", {
					required: { value: true, message: "An email address is required" },
					minLength: {
						value: 4,
						message: "Email must be at least 4 characters",
					},
					maxLength: {
						value: 80,
						message: "Email must be at most 80 characters",
					},
				})}
				error={errors.email}
			/>
			<Input
				type="password"
				className="w-full"
				placeholder="Password"
				minLength={10}
				id="password"
				required
				{...register("password", {
					minLength: {
						value: 10,
						message: "Password must be at least 10 characters",
					},
					maxLength: {
						value: 100,
						message: "Password must be at most 100 characters",
					},
					required: { value: true, message: "A password is required" },
				})}
				error={errors.password}
			/>
			<div className="space-y-1 py-3">
				<Checkbox
					id="consentTerms"
					title="Terms and Conditions consent checkbox"
					{...register("consentTerms", {
						required: {
							value: true,
							message: "You must consent to the Terms and Conditions",
						},
					})}
					error={errors.consentTerms}
				>
					I agree to the{" "}
					<a
						target="_blank"
						rel="noreferrer"
						href="/legal/terms-and-conditions"
					>
						Terms &amp; Conditions
					</a>{" "}
					and that I am 18+ years old
				</Checkbox>

				<Checkbox
					id="consentPrivacy"
					title="Privacy Policy consent checkbox"
					{...register("consentPrivacy", {
						required: {
							value: true,
							message: "You must consent to the Privacy Policy",
						},
					})}
					error={errors.consentPrivacy}
				>
					I agree to the{" "}
					<a target="_blank" rel="noreferrer" href="/legal/privacy-policy">
						Privacy Policy
					</a>
				</Checkbox>

				<Checkbox
					id="consentEula"
					title="EULA consent checkbox"
					{...register("consentEula", {
						required: {
							value: true,
							message: "You must consent to the EULA",
						},
					})}
				>
					I agree to the{" "}
					<a target="_blank" rel="noreferrer" href="/legal/eula">
						EULA
					</a>
				</Checkbox>

				<Checkbox
					title="Marketing consent checkbox"
					id="consentMarketing"
					{...register("consentMarketing")}
					error={errors.consentMarketing}
				>
					Subscribe me to promotional emails
				</Checkbox>
			</div>
			<hr className="mb-5" />
			<ClientOnly>
				<Turnstile
					error={errors["cf-turnstile-response"]}
					onTokenChange={setToken}
				/>
			</ClientOnly>

			<Button
				type="submit"
				className="w-full"
				disabled={!isValid || isSubmitting}
				loading={isSubmitting}
				loadingText="Processing Application..."
			>
				Create Agent Account
			</Button>
			<Button
				href="/auth/login"
				variant={ThemeButtonVariant.Outline}
				className="w-full mt-1"
			>
				Already have an account?
			</Button>
		</AuthForm>
	);
}
